import { api, AddBookingNew } from "common";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CarSelector } from "./components/CarSelector";
import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "components/alert/AlertContext";
import { PaymentSelector } from "./components/PaymentSelector";
import { ILocationResult } from "./components/Location/Search";
import { LocationSelector } from "./components/Location/Selector";
import { PassengerSelector } from "./components/PassengerSelector";
import { ArrowForward, ArrowBack, CheckCircle } from "@mui/icons-material";
import { RootState, IUserType, ITypeCarType, IBookingPaymentModeType, ITripDataType } from "common/src/types";
import { Box, Typography, Button, Divider, Stepper, Step, StepLabel, CircularProgress, StepButton } from "@mui/material";

interface IWaypoint {
    add: string;
    lat: number;
    lng: number;
}

const AddBooking = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [waypoints, setWaypoints] = useState<IWaypoint[]>([]);
    const [drop, setDrop] = useState<ILocationResult | null>(null);
    const [pickup, setPickup] = useState<ILocationResult | null>(null);
    const [selectedCar, setSelectedCar] = useState<ITypeCarType | null>(null);
    const [paymentMode, setPaymentMode] = useState<IBookingPaymentModeType>('cash');
    const [selectedPassenger, setSelectedPassenger] = useState<IUserType | null>(null);
    const [selectedType, setSelectedType] = useState<'pickup' | 'drop' | 'waypoints' | null>(null);

    const auth = useSelector((state: RootState) => state.auth);
    const usersdata = useSelector((state: RootState) => state.usersdata);
    const cartypes = useSelector((state: RootState) => state.cartypes.cars);
    const carlistdata = useSelector((state: RootState) => state.carlistdata);
    const settings = useSelector((state: RootState) => state.settingsdata.settings);


    const { fetchUsersOnce } = api;

    useEffect(() => {
        dispatch(fetchUsersOnce());
    }, [dispatch, fetchUsersOnce]);

    const passengers = usersdata.staticusers?.filter(
        (user: IUserType) =>
            user.usertype === 'customer'
    ) || [];

    const drivers = usersdata.staticusers?.filter(
        (user: IUserType) =>
            user.usertype === 'driver'
    ) || [];


    const activeDrivers = drivers?.filter(
        (driver: IUserType) => driver.driverActiveStatus === true && (!settings?.driver_approval || driver.approved === true) && driver.carType && driver.carType.length > 0
    ) || [];


    const cars = cartypes || [];

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);
    const handleReset = () => {
        setActiveStep(0);
        setSelectedPassenger(null);
        setPickup(null);
        setDrop(null);
        setWaypoints([]);
        setSelectedCar(null);
        setPaymentMode('cash');
    };

    const convertToLocationData = (location: ILocationResult | null) => {
        if (!location) return null;
        return {
            add: location.add,
            lat: String(location.lat),
            lng: String(location.lng),
        };
    };

    const handleSelectPickup = (location: ILocationResult) => {
        setPickup(location);
        setSelectedType('pickup');
    };

    const handleSelectDrop = (location: ILocationResult) => {
        setDrop(location);
        setSelectedType('drop');
    };

    const handleSelectWaypoint = (index: number) => {
        setSelectedType('waypoints');
    };

    const handleAddWaypoint = () => {
        if (!pickup?.add || !drop?.add) {
            showAlert({
                type: "WARNING",
                title: t("ADM_WARNING"),
                message: t("ADM_SELECT_PICKUP_AND_DROP_FIRST"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
            return;
        }

        const newWaypoint: IWaypoint = {
            add: '',
            lat: 0,
            lng: 0,
        };
        setWaypoints([...waypoints, newWaypoint]);
    };

    const handleRemoveWaypoint = (index: number) => {
        const updated = [...waypoints];
        updated.splice(index, 1);
        setWaypoints(updated);
    };

    const handleUpdateWaypoint = (index: number, location: ILocationResult) => {
        const updated = [...waypoints];
        updated[index] = {
            add: location.add,
            lat: location.lat,
            lng: location.lng
        };
        setWaypoints(updated);
    };

    const handleSelectCar = (car: ITypeCarType) => {
        setSelectedCar(car);
    };

    const handleCreateBooking = async () => {
        if (!pickup?.lat || !drop?.lat) {
            showAlert({
                type: "ERROR",
                title: t("ADM_ALERT"),
                message: t("ADM_SELECT_PICKUP_AND_DROP"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
            return;
        }

        if (!selectedPassenger) {
            showAlert({
                type: "ERROR",
                title: t("ADM_ALERT"),
                message: t("ADM_SELECT_PASSENGER"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
            return;
        }

        if (!drivers) {
            showAlert({
                type: "ERROR",
                title: t("ADM_ALERT"),
                message: t("ADM_SELECT_DRIVER"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
            return;
        }

        if (!selectedCar?.name) {
            showAlert({
                type: "ERROR",
                title: t("ADM_ALERT"),
                message: t("ADM_SELECT_CAR_TYPE"),
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
            return;
        }

        setLoading(true);

        try {
            const tripdata: ITripDataType = {
                pickup: {
                    lat: String(pickup.lat),
                    lng: String(pickup.lng),
                    add: pickup.add,
                    source: 'search',
                },
                drop: {
                    lat: String(drop.lat),
                    lng: String(drop.lng),
                    add: drop.add,
                    source: 'search',
                    waypoints: waypoints
                        .filter((wp) => wp.lat && wp.lng)
                        .map((wp) => ({
                            add: wp.add,
                            lat: wp.lat,
                            lng: wp.lng,
                            source: 'search',
                        })),
                },
                carType: selectedCar,
            };

            const result = await AddBookingNew({
                t,
                tripdata,
                isTaximeter: false,
                estimate: undefined,
                settings: settings!,
                drivers: activeDrivers,
                payment_mode: paymentMode,
                passenger: selectedPassenger,
            });

            if (result.error) {
                showAlert({
                    type: "ERROR",
                    title: t("ADM_ALERT"),
                    message: t(result.msg!),
                    buttons: [{ text: t("ADM_OK"), style: 'primary' }]
                });
                setLoading(false);
                return;
            }

            showAlert({
                type: "SUCCESS",
                title: t("ADM_SUCCESS"),
                message: t("ADM_BOOKING_CREATED_SUCCESSFULLY"),
                buttons: [
                    {
                        style: 'primary',
                        text: t("ADM_VIEW_BOOKING"),
                        onPress: () => {
                            if (result.bookingData?.id) {
                                navigate(`/bookings`);
                            }
                        }
                    },
                    { text: t("ADM_CLOSE"), style: 'secondary' }
                ]
            });

            handleReset();

        } catch (error) {
            console.error('Error creating booking:', error);
            showAlert({
                type: "ERROR",
                title: t("ADM_ALERT"),
                message: error as string,
                buttons: [{ text: t("ADM_OK"), style: 'primary' }]
            });
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        t("ADM_TRIP_DETAILS"),
        t("ADM_SELECT_PASSENGER"),
        t("ADM_SELECT_CAR_TYPE"),
        t("ADM_PAYMENT_METHOD"),
    ];

    const renderStep1 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <LocationSelector
                waypoints={waypoints}
                selected={selectedType}
                onSelectDrop={handleSelectDrop}
                onAddWaypoint={handleAddWaypoint}
                drop={convertToLocationData(drop)}
                onSelectPickup={handleSelectPickup}
                pickup={convertToLocationData(pickup)}
                onSelectWaypoint={handleSelectWaypoint}
                onRemoveWaypoint={handleRemoveWaypoint}
                onUpdateWaypoint={handleUpdateWaypoint}
            />

            <Box
                sx={{
                    p: 2,
                    bottom: 0,
                    mt: "auto",
                    borderTop: 1,
                    display: "flex",
                    position: "sticky",
                    borderColor: "divider",
                    justifyContent: "flex-end",
                    bgcolor: "background.paper",
                }}
            >
                <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                    disabled={!pickup?.lat || !drop?.lat}
                >
                    {t("ADM_NEXT")}
                </Button>
            </Box>
        </Box>
    );

    const renderStep2 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <PassengerSelector
                passengers={passengers}
                loading={usersdata.loading}
                selected={selectedPassenger}
                onSelect={setSelectedPassenger}
            />

            <Box
                sx={{
                    p: 2,
                    bottom: 0,
                    borderTop: 1,
                    display: "flex",
                    position: "sticky",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    justifyContent: "space-between",
                }}
            >
                <Button onClick={handleBack} startIcon={<ArrowBack />}>
                    {t("ADM_BACK")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                    disabled={(selectedPassenger?.firstName?.length ?? 0) < 1}
                >
                    {t("ADM_NEXT")}
                </Button>
            </Box>
        </Box>
    );

    const renderStep3 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

            <CarSelector
                cars={cars}
                onSelectCar={handleSelectCar}
                activeDrivers={activeDrivers}
                loading={carlistdata.loading}
                selectedCar={selectedCar?.name}
            />

            <Box
                sx={{
                    p: 2,
                    bottom: 0,
                    borderTop: 1,
                    display: "flex",
                    position: "sticky",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    justifyContent: "space-between",
                }}
            >
                <Button onClick={handleBack} startIcon={<ArrowBack />}>
                    {t("ADM_BACK")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!selectedCar}
                    endIcon={<ArrowForward />}
                >
                    {t("ADM_NEXT")}
                </Button>
            </Box>
        </Box>
    );

    const renderStep4 = () => (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

            <PaymentSelector
                settings={settings}
                selected={paymentMode}
                onSelect={setPaymentMode}
            />

            <Box
                sx={{
                    p: 2,
                    bottom: 0,
                    borderTop: 1,
                    display: "flex",
                    position: "sticky",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    justifyContent: "space-between",
                }}
            >
                <Button onClick={handleBack} startIcon={<ArrowBack />}>
                    {t("ADM_BACK")}
                </Button>
                <Button
                    color="success"
                    variant="contained"
                    onClick={handleCreateBooking}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                    {loading ? t("ADM_CREATING") : t("ADM_CREATE_BOOKING")}
                </Button>
            </Box>
        </Box>
    );

    const getStepContent = (step: number) => {
        switch (step) {
            case 0: return renderStep1();
            case 1: return renderStep2();
            case 2: return renderStep3();
            case 3: return renderStep4();
            default: return null;
        }
    };

    if (!settings || !auth?.profile) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "90dvh",
                display: "flex",
                borderRadius: 4,
                overflow: "hidden",
                flexDirection: "column",
                backgroundColor: "background.paper",
            }}
        >
            <Typography variant="h4" gutterBottom sx={{ p: 3 }}>
                {t("ADM_ADD_BOOKING")}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepButton onClick={() => setActiveStep(index)}>
                            <StepLabel>{label}</StepLabel>
                        </StepButton>
                    </Step>
                ))}
            </Stepper>

            <Divider />

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    overflow: "auto",
                    flexDirection: "column",
                }}
            >
                {getStepContent(activeStep)}
            </Box>
        </Box>
    );
};

export default AddBooking;